import React from 'react';
import { FormattedMessage } from 'react-intl';

import { CAMPAIGN_FILE_STATUS, CAMPAIGN_FILES_FILTERED_STATUSES } from 'components/campaigns/Details/constants';
import { CHANNEL, CONVERSATION_ENTITY_TYPE, SENDABLE_CHANNELS } from 'constants/ChatConstants';
import { ALL_STATUS, STATUSES } from 'constants/InterventionConstants';
import { isElementBefore } from 'utils/ArrayUtils';
import { getExtension } from 'utils/fileName';
import { humanize, isEmpty, ucFirst } from 'utils/global';
import { createDictionary } from 'utils/ObjectUtils';

export const DEFAULT_ERROR_MESSAGE = createDictionary(
  {
    'page.chat.details.sendbox.errors.emojisInSMS': 'SMS cannot contain emojis.',
    'validation.maxlength': 'The length of the entered value should not exceed more than {maxLength} characters',
  },
  'This field is mandatory'
);

/**
 * If media is coming from media library it will return the smallest size of the media. If not the media size.
 * @param {import("modules/medias/types/abstract-media").AbstractMedia} media
 * @returns {number | undefined}
 */
export function getMediaSize(media) {
  const sizes = Object.values(media.metadata?.size ?? {});

  if (sizes.length > 0) {
    return Math.min(...sizes);
  }

  return media.size;
}

/**
 * @param {string} content
 * @param {import('modules/global/typing/enums/messages.enum').MessageChannelEnum} channel
 * @returns {{id: string, defaultMessage: string} | undefined}
 */
export const getError = (content, channel) => {
  const hasEmoticon = /\p{Extended_Pictographic}/gu.test(content);

  if (hasEmoticon && channel === CHANNEL.SMS) {
    return 'page.chat.details.sendbox.errors.emojisInSMS';
  }

  return undefined;
};

/**
 * @param {string} value
 * @returns {import("antd/es/select").DefaultOptionType[]}
 */
export const optionChannelFormatter = value => ({
  value,
  'data-cy': `chat-channel-${value}`,
  label: (
    <FormattedMessage
      id={`page.chat.details.sendbox.action.channelTitle.${value}`}
      defaultMessage={`Notify by ${ucFirst(humanize(value))}`}
    />
  ),
});

const STATUSES_BY_ENTITY = createDictionary(
  {
    [CONVERSATION_ENTITY_TYPE.CAMPAIGN_FILE]: CAMPAIGN_FILES_FILTERED_STATUSES,
  },
  ALL_STATUS
);

const MINIMAL_STATUS_BY_ENTITY = createDictionary(
  {
    [CONVERSATION_ENTITY_TYPE.CAMPAIGN_FILE]: CAMPAIGN_FILE_STATUS.PENDING,
  },
  STATUSES.SENT
);

/**
 * Checks if the entity's status is invalid (i.e., before the minimal allowed status).
 * @param {Object} entity
 * @param {string} entityType
 * @returns {boolean}
 */
export const hasInvalidStatus = (entity, entityType) =>
  isElementBefore(STATUSES_BY_ENTITY[entityType], entity?.status, MINIMAL_STATUS_BY_ENTITY[entityType]);

/**
 * Returns the allowed chat channels for the given entity and entity type.
 * @param {import('modules/interventions/types/projections/detailed-intervention').DetailedIntervention | import('modules/campaignFiles/types').DetailedCampaignFile} entity
 * @param {string} entityType
 * @param {import("modules/global/typing/enums/steps.enum").ChannelEnum[]} customerChannels
 * @return {import("modules/global/typing/enums/steps.enum").ChannelEnum[]}
 */
export const getChatChannels = (entity, entityType, customerChannels) => {
  const channels = new Set(customerChannels);
  const conversationChannels = entity?.conversationContext?.channels ?? [];
  const haveBeeAlreadyContacted = conversationChannels.some(channel => SENDABLE_CHANNELS.includes(channel));

  // If the entity has a wrong status, only the INTERNAL channel is allowed
  if (!haveBeeAlreadyContacted && hasInvalidStatus(entity, entityType)) {
    channels.delete(CHANNEL.INTERNAL);
  }

  return [...channels];
};

/**
 * @param {import("modules/medias/types/abstract-media").AbstractMedia} media
 * @param {Record<string, number>} sizes
 * @param {string} channel
 * @returns {React.ReactNode | undefined} - Returns an error message if the media size exceeds the limit, otherwise undefined.
 */
const mediaSizeValidator = (media, sizes, channel) => {
  const { type: mediaType } = media;
  const maxFileSize = sizes[mediaType];
  const mediaSize = getMediaSize(media) ?? media.size;

  if (maxFileSize && mediaSize > maxFileSize * 1024 * 1024) {
    return (<FormattedMessage
      id="chat.details.sendbox.errors.file_too_big"
      defaultMessage="This media exceeds the authorized size ({maxFileSize} MB) for {channel}"
      values={{ maxFileSize, channel: <FormattedMessage id={`channels.${channel}`} defaultMessage={channel} /> }}
    />);
  }
  return undefined;
};

const mediaDurationValidator = (media, durations, channel) => {
  const maxDuration = durations?.[media.type];

  if (maxDuration && media.time > maxDuration) {
    return (
      <FormattedMessage
        id="chat.details.sendbox.errors.duration"
        defaultMessage="This media exceeds the allowed duration ({maxDuration} seconds) for {channel}"
        values={{
          channel: <FormattedMessage
            id={`channels.${channel}`}
            defaultMessage={channel}
          />,
          maxDuration }}
      />
    );
  }
  return undefined;
};

/**
 * @param {import("modules/medias/types/abstract-media").AbstractMedia} media
 * @param {string[]} extensions
 * @param {string} channel
 * @returns {React.ReactNode | undefined}
 */
const mediaExtensionValidator = (media, extensions, channel) => {
  const extension = getExtension(media.filename)?.toLowerCase();
  if (!extensions.includes(extension)) {
    return (
      <FormattedMessage
        id="chat.details.sendbox.errors.file_extension"
        defaultMessage="This media is an unsupported extension for {channel}"
        values={{ channel: <FormattedMessage id={`channels.${channel}`} defaultMessage={channel} /> }}
      />
    );
  }
  return undefined;
};

/**
 * Validates media files against size and extension criteria.
 * @param {import("modules/medias/types/abstract-media").AbstractMedia[]} mediaList
 * @param {{durations:Record<string,number>, extensions:string[], sizes:Record<string,number>}} configuration
 * @param {string} channel
 * @returns {Record<string, React.ReactNode> | undefined}
 */
export const computeMediaErrors = (mediaList, configuration, channel) => {
  const { durations, extensions, maxFileSize: sizes } = configuration;

  const errors = mediaList.reduce((acc, media) => {
    const mediaError =
        mediaSizeValidator(media, sizes, channel) ??
        mediaDurationValidator(media, durations, channel) ??
        mediaExtensionValidator(media, extensions, channel);
    if (mediaError) {
      return {
        ...acc,
        [media.id]: mediaError,
      };
    }
    return acc;
  }, {});
  return isEmpty(errors) ? undefined : errors;
};
